import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "image-lightbox__container" }
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = {
  key: 0,
  class: "image-lightbox__loading"
}
const _hoisted_4 = {
  key: 1,
  class: "image-lightbox__error"
}
const _hoisted_5 = {
  key: 2,
  class: "image-lightbox__counter"
}

import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { Icon } from "@iconify/vue";
import baseAPI from "@/api/base";

interface Props {
  show: boolean;
  images: string[];
  currentIndex?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ImageLightbox',
  props: {
    show: { type: Boolean },
    images: {},
    currentIndex: { default: 0 }
  },
  emits: ["close", "update:currentIndex"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const currentIndex = ref(props.currentIndex);
const isLoading = ref(false);
const imageError = ref(false);
const imageBlobUrls = ref<Map<string, string>>(new Map());

const hasMultipleImages = computed(() => props.images.length > 1);

const isApiUrl = (url: string): boolean => {
  return url.startsWith("/unipile/") || url.startsWith("/api/");
};

const loadImageAsBlob = async (url: string): Promise<string> => {
  if (imageBlobUrls.value.has(url)) {
    return imageBlobUrls.value.get(url)!;
  }

  if (isApiUrl(url)) {
    try {
      const response = await baseAPI.get(url, { responseType: "blob" });
      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      imageBlobUrls.value.set(url, blobUrl);
      return blobUrl;
    } catch (error) {
      console.error("Failed to load image from API:", error);
      throw error;
    }
  }

  return url;
};

const currentImageUrl = computed(() => {
  if (props.images.length === 0) return "";
  const url = props.images[currentIndex.value] || props.images[0];
  return imageBlobUrls.value.get(url) || url;
});

const currentImageAlt = computed(() => `Image ${currentIndex.value + 1} of ${props.images.length}`);

const close = () => {
  emit("close");
};

const previousImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    emit("update:currentIndex", currentIndex.value);
    imageError.value = false;
  } else if (hasMultipleImages.value) {
    currentIndex.value = props.images.length - 1;
    emit("update:currentIndex", currentIndex.value);
    imageError.value = false;
  }
};

const nextImage = () => {
  if (currentIndex.value < props.images.length - 1) {
    currentIndex.value++;
    emit("update:currentIndex", currentIndex.value);
    imageError.value = false;
  } else if (hasMultipleImages.value) {
    currentIndex.value = 0;
    emit("update:currentIndex", currentIndex.value);
    imageError.value = false;
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.show) return;

  switch (event.key) {
    case "Escape":
      close();
      break;
    case "ArrowLeft":
      previousImage();
      break;
    case "ArrowRight":
      nextImage();
      break;
  }
};

const handleImageError = () => {
  imageError.value = true;
  isLoading.value = false;
};

watch(
  () => props.currentIndex,
  (newIndex) => {
    currentIndex.value = newIndex;
    imageError.value = false;
  }
);

const loadCurrentImage = async () => {
  if (props.images.length === 0) return;

  const url = props.images[currentIndex.value] || props.images[0];
  imageError.value = false;
  isLoading.value = true;

  try {
    let imageUrl = url;

    if (isApiUrl(url)) {
      imageUrl = await loadImageAsBlob(url);
    }

    const img = new Image();
    img.onload = () => {
      isLoading.value = false;
    };
    img.onerror = () => {
      isLoading.value = false;
      imageError.value = true;
    };
    img.src = imageUrl;
  } catch (error) {
    isLoading.value = false;
    imageError.value = true;
  }
};

watch(
  () => currentIndex.value,
  () => {
    if (props.show) {
      loadCurrentImage();
    }
  }
);

watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      loadCurrentImage();
    }
  }
);

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  imageBlobUrls.value.forEach((blobUrl) => {
    window.URL.revokeObjectURL(blobUrl);
  });
  imageBlobUrls.value.clear();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "lightbox-fade" }, {
      default: _withCtx(() => [
        (__props.show)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "image-lightbox",
              onClick: _withModifiers(close, ["self"])
            }, [
              _createElementVNode("button", {
                class: "image-lightbox__close",
                onClick: close,
                "aria-label": "Close"
              }, [
                _createVNode(_unref(Icon), { icon: "mdi:close" })
              ]),
              (hasMultipleImages.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "image-lightbox__nav image-lightbox__nav--prev",
                    onClick: previousImage,
                    "aria-label": "Previous image"
                  }, [
                    _createVNode(_unref(Icon), { icon: "mdi:chevron-left" })
                  ]))
                : _createCommentVNode("", true),
              (hasMultipleImages.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    class: "image-lightbox__nav image-lightbox__nav--next",
                    onClick: nextImage,
                    "aria-label": "Next image"
                  }, [
                    _createVNode(_unref(Icon), { icon: "mdi:chevron-right" })
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("img", {
                  src: currentImageUrl.value,
                  alt: currentImageAlt.value,
                  class: "image-lightbox__image",
                  onError: handleImageError
                }, null, 40, _hoisted_2),
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:loading",
                        class: "image-lightbox__loading-spinner"
                      }),
                      _cache[0] || (_cache[0] = _createElementVNode("span", null, "Loading image...", -1))
                    ]))
                  : _createCommentVNode("", true),
                (imageError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createVNode(_unref(Icon), { icon: "mdi:image-off" }),
                      _cache[1] || (_cache[1] = _createElementVNode("span", null, "Failed to load image", -1))
                    ]))
                  : _createCommentVNode("", true)
              ]),
              (hasMultipleImages.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(currentIndex.value + 1) + " / " + _toDisplayString(__props.images.length), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})